<template>
  <div class="app-search flex items-center">
    <Sheet v-model:open="open">
      <SheetTrigger
        class="group rounded-full bg-dark-background p-2 text-light hover:text-primary-hover transition-colors duration-300"
        @click="searchBtnOpen"
      >
        <IconsSearch class="h-8 w-8" />
      </SheetTrigger>
      <SheetContent class="app-search-content border-none" overlay-class="z-50" :side="isAr ? 'left' : 'right'">
        <SheetHeader class="flex-row px-4">
          <SearchInput
            v-model="searchVal"
            v-model:trigger="searchTrigger"
            class="flex-1"
            :placeholder="formatTransify('CF_HOME_SEARCH')"
          />
          <SheetClose class="app-search-close rtl:rotate-180">
            <IconsReturn class="h-8 w-8" />
          </SheetClose>
        </SheetHeader>
        <SearchWrapperTitle v-show="searchStatus === 0" :data-state="searchStatus" class="mb-4 mt-10 text-light px-4">
          <span class="truncate text-2xl ...">{{ searchResultText }}</span>
          <GfrNewButton
            class="inline-flex h-8 w-8 flex-shrink-0 text-light hover:text-white ltr:ml-4 rtl:mr-4"
            @click="clearHistory"
          >
            <IconsDelete class="h-full w-full transition-colors duration-200" />
          </GfrNewButton>
        </SearchWrapperTitle>
        <SearchTabs
          v-if="selectOptions.length > 2 && searchStatus === 2"
          class="mb-4 mt-12 px-4"
          v-model="selectVal"
          :tabs="selectOptions"
        />
        <GfrScrollArea
          v-model:reset="resetScroll"
          class="mt-4 w-full flex-1 px-4"
          :offset="{ bottom: 60 }"
          :dir="isAr ? 'rtl' : 'ltr'"
          @scroll-end="loadMore"
        >
          <div v-show="searchStatus === 1" class="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
            <IconsLoading class="h-18 w-18 animate-spin" />
          </div>
          <SearchHistory
            v-show="searchStatus === 0"
            :data="searchHistory"
            @clear-history="clearHistory"
            @select-history="selectFromHistory"
          />
          <SearchResult
            v-show="searchStatus === 2"
            ref="resultRef"
            :page="searchResultPage"
            :page-counts="searchResultPageCounts"
            :data="searchResults"
            :current-category="selectVal"
            @visit="visitFormResult"
          />
        </GfrScrollArea>
      </SheetContent>
    </Sheet>
  </div>
</template>
<script lang="ts" setup>
import './styles/index.scss'
import SearchInput from './Input.vue'
import SearchHistory from './History.vue'
import SearchWrapperTitle from './WrapperTitle.vue'
import SearchTabs from './Tabs.vue'
import SearchResult from './Result.vue'
import { Sheet, SheetContent, SheetTrigger } from '~/components/ui/sheet'
import IconsReturn from '~/components/icons/Return.vue'
import { SearchGlobalData } from '~/server/types'
const { isAr } = useLang()

const route = useRoute()
const gtag = useGtag()
const store = useStore()
const { formatTransify } = store
const searchStore = useSearchStore()
const { fetchSearchData } = searchStore
const { selectOptions } = storeToRefs(searchStore)
const open = ref(false)
const selectVal = ref(0) // 输入搜索内容
const searchHistory = ref<string[]>([]) // 搜索历史记录列表，在localstorage中读写
const searchTrigger = ref(false) // 搜索是否触发true 调用搜索api
const searchVal = ref('') // 搜索词
const searchResults = ref<SearchGlobalData[]>([]) // 搜索结果列表
const searchResultCounts = ref(0) // 搜索结果数量
const searchStatus = ref<0 | 1 | 2>(0) // 0: no search, 1: searching, 2: show search result
const searchResultPage = ref(1)
const searchResultPageCounts = ref(25)
const resetScroll = ref(false) // reset scroll area
const searchResultText = computed(() => {
  if (searchStatus.value === 2) {
    return formatTransify('UGC_OFFICIAL_WEBSITE_SEARCH_RESULTS')
  } else if (searchStatus.value === 0) {
    return formatTransify('UGC_OFFICIAL_WEBSITE_SEARCH_RECENT')
  }
  return ''
})
const searchBtnOpen = () => {
  // gtag event -  search icon click
  gtag('event', `cl_search_icon_click`, {
    event_category: 'Search',
    event_action: 'Click',
    event_label: true
  })
}
/* --- fetch --- */
const search = async () => {
  const searchValue = searchVal.value.trim()
  if (searchValue === '') return false
  searchStatus.value = 1
  // gtag event -  search request
  gtag('event', `cl_search_data`, {
    event_category: 'Search',
    event_action: 'Request',
    event_label: true,
    value: searchValue
  })
  const res = await fetchSearchData(searchValue, '0')
  searchResultPage.value = 1
  searchResults.value = res.data?.data || []
  searchResultCounts.value = res.data?.count || 0
  searchHistory.value = searchHistory.value.slice(0, 49)
  searchHistory.value.unshift(searchValue)
  searchHistory.value = Array.from(new Set(searchHistory.value))
  storage.setItem('searchHistory', JSON.stringify(searchHistory.value))
  setTimeout(() => {
    searchStatus.value = 2
  }, 300)
}
/* --- handle history --- */
const selectFromHistory = (item: string) => {
  searchVal.value = item.trim()
  searchTrigger.value = true
  // gtag event -  search history click
  gtag('event', `cl_search_history_click`, {
    event_category: 'Search',
    event_action: 'Click',
    event_label: true,
    value: searchVal.value
  })
}
const clearHistory = () => {
  searchHistory.value = []
  storage.removeItem('searchHistory')
}
const initHistory = () => {
  const history = JSON.parse(storage.getItem('searchHistory') || '[]').slice(0, 50)
  searchHistory.value = Array.from(new Set(history))
}
/* ---- Search Results ---- */
const visitFormResult = (title: string) => {
  // gtag event -  visit search result - value = searchVal + search result title
  gtag('event', `cl_search_result_page_click`, {
    event_category: 'Search',
    event_action: 'Click',
    event_label: true,
    value: `${searchVal.value.trim()} + ${title.replace(/<[^>]*>?/gm, '')}`
  })
}
const resetSearch = () => {
  searchVal.value = ''
  selectVal.value = 0
  searchStatus.value = 0
  searchTrigger.value = false
}
const loadMore = () => {
  if (searchStatus.value !== 2) return
  searchResultPage.value++
}
/* --- search trigger while true, reset search status while false --- */
watch(
  () => searchTrigger.value,
  (val) => {
    if (val) {
      search()
    } else {
      resetSearch()
    }
  }
)
/* --- reset scroll bar while changing searchStatus or selectVal  --- */
watch(
  () => [searchStatus.value, selectVal.value],
  ([_, selectVal]) => {
    resetScroll.value = true
    if (selectVal !== 0) {
      // gtag event -  search result select category
      gtag('event', `cl_search_result_select_category`, {
        event_category: 'Search',
        event_action: 'Toggle',
        event_label: true,
        value: selectVal
      })
    }
  }
)
/* --- reset search status after closing --- */
watch(
  () => open.value,
  (val) => {
    if (!val) {
      resetSearch()
    }
  }
)
/* --- resetSearch while page change --- */
watch(
  () => route.path,
  () => {
    open.value = false
  }
)

onMounted(() => {
  initHistory()
})
</script>
